import { useEffect, type RefObject } from 'react'
import * as Cesium from 'cesium'
import type { CdRegionMode } from '../../types/changeDetection'

/** `POLYGON((lon lat, ...))` 의 바깥 링을 [lon, lat][] 로 파싱한다. 형식이 다르면 null. */
export function parsePolygonWkt(wkt: string): [number, number][] | null {
  const m = /POLYGON\s*\(\(([^)]*)\)/i.exec(wkt)
  if (!m) return null
  const ring = m[1]
    .split(',')
    .map((pair) => pair.trim().split(/\s+/).map(Number))
    .filter((p) => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map((p) => [p[0], p[1]] as [number, number])
  return ring.length >= 3 ? ring : null
}

/**
 * 프로젝트에 저장된 voxel 생성 영역을 지도에 윤곽선으로 표시한다.
 * wkt 가 바뀌거나 사라지거나 훅이 언마운트되면 걷어낸다.
 */
export function useCdRegionOverlay(
  viewerRef: RefObject<Cesium.Viewer | null>,
  wkt: string | null,
  enabled: boolean,
  mode: CdRegionMode = 'INCLUDE',
) {
  useEffect(() => {
    const v = viewerRef.current
    if (!enabled || !wkt || !v || v.isDestroyed()) return
    const ring = parsePolygonWkt(wkt)
    if (!ring) return

    const color = Cesium.Color.fromCssColorString(mode === 'EXCLUDE' ? '#ef4444' : '#f97316')
    const positions = Cesium.Cartesian3.fromDegreesArray(ring.flat())
    const entity = v.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(positions),
        material: color.withAlpha(0.08),
        classificationType: Cesium.ClassificationType.TERRAIN,
      },
      polyline: {
        positions: [...positions, positions[0]],
        width: 2,
        material: new Cesium.PolylineDashMaterialProperty({ color }),
        clampToGround: true,
      },
    })

    return () => {
      if (!v.isDestroyed()) v.entities.remove(entity)
    }
  }, [viewerRef, wkt, enabled, mode])
}
